import type { SymbolSearchResult } from '@shared/types/symbol'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { SearchInput } from './SearchInput'
import { SearchResults } from './SearchResults'

import { useSearch } from '@/hooks/useSearch'
import { useChartStore } from '@/stores/chartStore'
import { cn } from '@/utils/cn'

interface GlobalSearchProps {
  /** Controlled open state from parent */
  open: boolean
  onClose: () => void
}

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const overlayRef = useRef<HTMLDivElement>(null)

  const { results, labels, isLoading, query: searchedQuery } = useSearch(query)
  const { activePanelId, setSymbol } = useChartStore()

  // Flatten grouped results for keyboard navigation
  const flatResults = useMemo<SymbolSearchResult[]>(() => Object.values(results).flat(), [results])

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [open])

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0)
  }, [searchedQuery])

  // Scroll active item into view
  useEffect(() => {
    const el = document.getElementById(`search-result-${activeIndex}`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleSelect = useCallback(
    (result: SymbolSearchResult) => {
      setSymbol(activePanelId, result.ticker)
      onClose()
    },
    [activePanelId, setSymbol, onClose]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, flatResults.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter' && flatResults[activeIndex]) {
        e.preventDefault()
        handleSelect(flatResults[activeIndex])
      } else if (e.key === 'Escape') {
        onClose()
      }
    },
    [flatResults, activeIndex, handleSelect, onClose]
  )

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-label="Symbol search"
        className={cn(
          'relative z-10 w-full max-w-xl overflow-hidden rounded-lg shadow-2xl',
          'border border-tv-border bg-tv-bg-secondary',
          'animate-slide-down'
        )}
      >
        {/* Divider line at top of results */}
        <SearchInput
          value={query}
          onChange={(v) => {
            setQuery(v)
            setActiveIndex(0)
          }}
          onClose={onClose}
          className="border-b border-tv-border"
        />

        <SearchResults
          groups={results}
          labels={labels}
          query={query}
          activeIndex={activeIndex}
          flatResults={flatResults}
          onSelect={handleSelect}
          onHoverIndex={setActiveIndex}
          isLoading={isLoading}
        />

        {/* Footer hint */}
        <div className="flex items-center gap-3 border-t border-tv-border px-4 py-2 text-2xs text-tv-text-muted">
          <span>
            <kbd className="font-mono">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="font-mono">↵</kbd> select
          </span>
          <span>
            <kbd className="font-mono">Esc</kbd> close
          </span>
        </div>
      </div>
    </div>,
    document.body
  )
}
